import { useEffect, useState, useRef } from 'react';
import { Calendar, MapPin, Clock, ArrowRight, Navigation, ChevronDown, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEventDetails } from '../../../../hooks/useEventDetails';
import { useEventContext } from '../../../contexts/EventContext';
import { useCurrentUser } from '../../../../hooks/useCurrentUser';
import { useParticipantCheck } from '../../../../hooks/useParticipantCheck';
import { V1SectionRenderer } from '../sections/V1SectionRenderer';
import { formatEventDateTime } from '../../../../utils/formatEventDateTime';
import moment from 'moment-timezone';

/**
 * V1 Event Details Page - FROZEN
 *
 * This component displays details for V1 events only.
 * It is a simplified version without V2 features like:
 * - Custom sections
 * - Hero configurations
 * - Keywords/tags
 * - Social media integration
 * - Sponsor/exhibitor previews
 *
 * DO NOT add V2 features to this component.
 * This component will be removed after V1 sunset.
 */
export const DetailsPageV1 = () => {
  const { eventSlug, style: contextStyle } = useEventContext();
  const { data: eventDetails, isLoading: eventLoading, error: eventError } = useEventDetails(eventSlug);
  const { currentUser } = useCurrentUser();
  const { data: participantCheck } = useParticipantCheck(eventSlug);
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if user is a participant using the lightweight check
  const isParticipant = participantCheck?.isParticipant || false;

  // Get the primary color from event style or context (same as NavbarV1)
  // Check if contextStyle has actual style data (not just an empty object)
  const eventStyle = (contextStyle && contextStyle.headerColor) ? contextStyle : eventDetails?.style;
  const primaryColor = eventStyle?.headerColor || '#000000';



  // Update page title
  useEffect(() => {
    if (eventDetails) {
      document.title = `Details - ${eventDetails.name}`;
    }
  }, [eventDetails]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCalendarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (eventLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (eventError || !eventDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600">Error loading event details</p>
        </div>
      </div>
    );
  }

  // Generate Google Maps embed URL
  const getMapUrl = () => {
    if (!eventDetails.location) return null;

    const address = [
      eventDetails.location.name,
      eventDetails.location.address1,
      eventDetails.location.city,
      eventDetails.location.state,
      eventDetails.location.zip
    ].filter(Boolean).join(', ');

    // If no valid address components, don't return a map URL
    if (!address.trim()) return null;

    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(address)}`;
  };

  // Format date with full details
  const formatFullDateTime = (date: string, time?: string, showTime?: boolean, timezone?: string) => {
    const dateTime = time && showTime
      ? moment.tz(`${moment(date).format('YYYY-MM-DD')} ${moment(time).format('HH:mm:ss')}`, timezone || moment.tz.guess())
      : moment(date);

    return dateTime.format('ddd, MMM D, YYYY, h:mm A z');
  };

  // Format date range for display
  const formatDateRange = () => {
    if (!eventDetails.startsAt) return '';

    const start = moment(eventDetails.startsAt);
    const end = eventDetails.endsAt ? moment(eventDetails.endsAt) : start;

    // Format like "Mon, Dec 8, 2025, 4:30 PM PST — Wed, Dec 10, 2025, 10:30 AM PST"
    const startFormat = start.format('ddd, MMM D, YYYY, h:mm A z');
    const endFormat = end.format('ddd, MMM D, YYYY, h:mm A z');

    if (start.isSame(end, 'day')) {
      // Same day - show single date with time range
      return `${start.format('ddd, MMM D, YYYY')}, ${start.format('h:mm A')} — ${end.format('h:mm A z')}`;
    }

    return `${startFormat} — ${endFormat}`;
  };

  // Generate calendar URL for the event
  const generateEventCalendarUrl = (calendarType: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => {
    if (!eventDetails || !eventDetails.startsAt) return '#';

    const start = moment.tz(eventDetails.startsAt, eventDetails.timeZone || moment.tz.guess());
    const end = eventDetails.endsAt ? moment.tz(eventDetails.endsAt, eventDetails.timeZone || moment.tz.guess()) : start.clone().add(1, 'hour');

    const formatGoogleDate = (date: moment.Moment) => {
      return date.utc().format('YYYYMMDDTHHmmss') + 'Z';
    };

    const formatICSDate = (date: moment.Moment) => {
      return date.utc().format('YYYYMMDDTHHmmss') + 'Z';
    };

    const title = encodeURIComponent(eventDetails.name);
    const location = eventDetails.location ?
      encodeURIComponent(`${eventDetails.location.name || ''} ${eventDetails.location.fullAddress || ''}`.trim()) :
      '';
    const details = encodeURIComponent(eventDetails.description || '');

    switch (calendarType) {
      case 'google':
        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(start)}/${formatGoogleDate(end)}&details=${details}&location=${location}`;

      case 'office365':
      case 'outlookcom':
        return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&location=${location}&startdt=${start.utc().format()}&enddt=${end.utc().format()}&allday=false`;

      case 'outlook':
        return `https://outlook.office.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&location=${location}&startdt=${start.utc().format()}&enddt=${end.utc().format()}&allday=false`;

      case 'yahoo':
        const yahooStart = start.format('YYYYMMDDTHHmmss');
        const yahooEnd = end.format('YYYYMMDDTHHmmss');
        return `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${title}&st=${yahooStart}&et=${yahooEnd}&desc=${details}&in_loc=${location}`;

      case 'apple':
        const icsContent = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//MessageBlocks//Event//EN',
          'BEGIN:VEVENT',
          `DTSTART:${formatICSDate(start)}`,
          `DTEND:${formatICSDate(end)}`,
          `SUMMARY:${decodeURIComponent(title)}`,
          `DESCRIPTION:${decodeURIComponent(details)}`,
          `LOCATION:${decodeURIComponent(location)}`,
          `UID:${eventDetails.id}@messageblocks.com`,
          'END:VEVENT',
          'END:VCALENDAR'
        ].join('\r\n');

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        return URL.createObjectURL(blob);

      default:
        return '#';
    }
  };

  // Handle calendar selection
  const handleAddToCalendar = (type: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => {
    const url = generateEventCalendarUrl(type);

    if (type === 'apple') {
      // For Apple Calendar, download the .ics file
      const link = document.createElement('a');
      link.href = url;
      link.download = `${eventDetails.name.replace(/\s+/g, '-')}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // For other calendars, open in new tab
      window.open(url, '_blank');
    }

    setShowCalendarDropdown(false);
  };




  return (
    <div className="min-h-screen">
      {/* Hero wrapper with background */}
      <div className="relative">
        {/* Responsive background - full width on mobile, half width on desktop (unless no image) */}
        <div
          className={`event-details-hero-right-filter absolute inset-0 bg-gray-50/90 ${
            eventDetails.eventHeaderUrl
              ? 'lg:inset-auto lg:top-0 lg:right-0 lg:bottom-0 lg:left-1/2 lg:-ml-12 lg:rounded-br-2xl'
              : 'rounded-b-2xl'
          }`}
        />

        {/* Event Header/Hero Section */}
        <div className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 lg:pt-16 lg:pt-20 pb-12">
          <div className="flex flex-col lg:flex-row items-center lg:items-start">
            {/* Left side - Image aligned with main content */}
            {eventDetails.eventHeaderUrl && (
              <div className="flex-shrink-0 w-full lg:w-auto">
                <div
                  className="relative rounded-lg shadow-lg overflow-hidden bg-white z-20 w-full lg:w-[600px] aspect-[2/1]"
                >
                  {/* Blurred background image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center blur-xl scale-110 opacity-15"
                    style={{
                      backgroundImage: `url(${eventDetails.eventHeaderUrl})`
                    }}
                  />
                  {/* Main image */}
                  <img
                    src={eventDetails.eventHeaderUrl}
                    alt={eventDetails.name}
                    className="absolute inset-0 w-full h-full object-contain z-10"
                  />
                </div>
              </div>
            )}

            {/* Right side - Event Info */}
            <div className={`space-y-4 ${eventDetails.eventHeaderUrl ? 'mt-8 lg:mt-0 lg:ml-12' : 'text-center mx-auto max-w-2xl'}`}>
              <h2 className="text-sm text-gray-600 font-normal">
                <span className="textFitted">
                  {eventDetails.startsAt && formatEventDateTime({
                  startsAt: eventDetails.startsAt,
                  endsAt: eventDetails.endsAt || eventDetails.startsAt,
                  showStartTime: eventDetails.showStartTime,
                  showEndTime: eventDetails.showEndTime,
                  timeZone: eventDetails.ianaTimeZone || eventDetails.timeZone,
                  timeFormat: '12_hour',
                  long: true
                  })}
                </span>
              </h2>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
                <span className="textFitted">{eventDetails.name}</span>
              </h1>
              {eventDetails.eventHeader && (
                <h2 className="text-lg text-gray-700 font-normal"><span className="textFitted">{eventDetails.eventHeader}</span></h2>
              )}

              {/* Registration/Enter Event CTA */}
              <div className="pt-4">
                {isParticipant ? (
                  <a
                    href={`/accounts/events/${eventSlug}`}
                    className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg transition-colors font-semibold max-w-2xs"
                    style={{
                      backgroundColor: primaryColor || '#2563eb',
                      color: '#ffffff'
                    }}
                  >
                    Enter {eventDetails?.team?.eventName || 'Event'}
                    <ArrowRight className="w-5 h-5" />
                </a>
                ) : eventDetails.registerable && (
                  <a
                    href={`/${eventSlug}/registrations/new`}
                    className="inline-flex items-center px-6 py-3 text-white font-semibold rounded transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: primaryColor || '#2563eb',
                      color: '#ffffff'
                    }}
                  >
                    {eventDetails.eventOptions?.registerCallToAction || eventDetails.eventOptions?.registrationName || 'Register Now'}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 -mt-2 max-w-6xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8 -mt-8">
            {/* Content Page Sections - Show when content page exists */}
            {eventDetails.contentPage?.active && eventDetails.contentPage.sections?.length > 0 ? (
              <>
                {eventDetails.contentPage.sections
                  .filter((section: any) => section.active)
                  .sort((a: any, b: any) => a.position - b.position)
                  .map((section: any) => (
                    <V1SectionRenderer
                      key={section.id}
                      section={section}
                    />
                  ))}
              </>
            ) : (
              /* Event Info Card - Only show when no content page data */
              <div className="relative bg-white rounded-lg shadow-md mt-8">
                {/* Pseudo-element container for rounded corners */}
                <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
                  {/* Top accent border */}
                  <div className="h-1" style={{ backgroundColor: primaryColor }} />
                </div>
                <div className="relative p-6 pt-7">
                  {/* Event Description */}
                  {eventDetails.description && (
                    <div className="event-description">
                      <div dangerouslySetInnerHTML={{ __html: eventDetails.description }} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - When & Where */}
          <div className="space-y-6">
            {/* When Card */}
            <div className="relative bg-white rounded-lg shadow-md">
              {/* Pseudo-element container for rounded corners */}
              <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
                {/* Top accent border */}
                <div className="h-1" style={{ backgroundColor: primaryColor }} />
              </div>
              <div className="relative p-6 pt-7">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  When
                </h2>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowCalendarDropdown(!showCalendarDropdown)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Add to calendar"
                  >
                    <Calendar className="h-5 w-5 text-gray-600" />
                  </button>
                  {showCalendarDropdown && (
                    <div className="absolute right-0 top-full mt-2 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
                      <button
                        onClick={() => handleAddToCalendar('apple')}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        {eventDetails.calendarIcons?.apple ? (
                          <img src={eventDetails.calendarIcons.apple} alt="Apple" className="w-4 h-4" />
                        ) : (
                          <span className="w-4 h-4 text-gray-400">🍎</span>
                        )}
                        Apple
                      </button>
                      <button
                        onClick={() => handleAddToCalendar('google')}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        {eventDetails.calendarIcons?.google ? (
                          <img src={eventDetails.calendarIcons.google} alt="Google" className="w-4 h-4" />
                        ) : (
                          <span className="w-4 h-4 text-gray-400">📅</span>
                        )}
                        Google <span className="text-gray-400 text-xs">(online)</span>
                      </button>
                      <button
                        onClick={() => handleAddToCalendar('office365')}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        {eventDetails.calendarIcons?.office365 ? (
                          <img src={eventDetails.calendarIcons.office365} alt="Office 365" className="w-4 h-4" />
                        ) : (
                          <span className="w-4 h-4 text-gray-400">📊</span>
                        )}
                        Office 365 <span className="text-gray-400 text-xs">(online)</span>
                      </button>
                      <button
                        onClick={() => handleAddToCalendar('outlook')}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        {eventDetails.calendarIcons?.outlook ? (
                          <img src={eventDetails.calendarIcons.outlook} alt="Outlook" className="w-4 h-4" />
                        ) : (
                          <span className="w-4 h-4 text-gray-400">📧</span>
                        )}
                        Outlook
                      </button>
                      <button
                        onClick={() => handleAddToCalendar('outlookcom')}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        {eventDetails.calendarIcons?.outlookcom ? (
                          <img src={eventDetails.calendarIcons.outlookcom} alt="Outlook.com" className="w-4 h-4" />
                        ) : (
                          <span className="w-4 h-4 text-gray-400">📧</span>
                        )}
                        Outlook.com <span className="text-gray-400 text-xs">(online)</span>
                      </button>
                      <button
                        onClick={() => handleAddToCalendar('yahoo')}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                      >
                        {eventDetails.calendarIcons?.yahoo ? (
                          <img src={eventDetails.calendarIcons.yahoo} alt="Yahoo" className="w-4 h-4" />
                        ) : (
                          <span className="w-4 h-4 text-gray-400">📮</span>
                        )}
                        Yahoo <span className="text-gray-400 text-xs">(online)</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="text-center mr-4 bg-gray-50 rounded-lg p-3 min-w-[60px]">
                    <div className="text-gray-600 text-sm uppercase">
                      {eventDetails.startsAt && moment(eventDetails.startsAt).format('MMM')}
                    </div>
                    <div className="text-3xl font-bold">
                      {eventDetails.startsAt && moment(eventDetails.startsAt).format('DD')}
                    </div>
                  </div>
                  <div className="flex-1 text-sm text-gray-700">
                    <div className="font-medium">
                      {eventDetails.startsAt && formatEventDateTime({
                        startsAt: eventDetails.startsAt,
                        endsAt: eventDetails.endsAt || eventDetails.startsAt,
                        showStartTime: eventDetails.showStartTime,
                        showEndTime: eventDetails.showEndTime,
                        timeZone: eventDetails.ianaTimeZone || eventDetails.timeZone,
                        timeFormat: '12_hour',
                        long: false
                      })}
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Where Card - Only show if location has meaningful data AND not hidden by V1 option */}
            {!eventDetails.hideEventDetailsLocation && eventDetails.location && (eventDetails.location.name || eventDetails.location.address1 || eventDetails.location.cityStateZip) && (
              <div className="relative bg-white rounded-lg shadow-md">
                {/* Pseudo-element container for rounded corners */}
                <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
                  {/* Top accent border */}
                  <div className="h-1" style={{ backgroundColor: primaryColor }} />
                </div>
                <div className="relative p-6 pt-7">
                  <h2 className="text-xl font-bold mb-4 flex items-center justify-between">
                    <span className="flex items-center">
                      <MapPin className="mr-2 h-5 w-5" />
                      Where
                    </span>
                    {eventDetails.location.directionsUrl && (
                      <a
                        href={eventDetails.location.directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        Map Location
                        <Navigation className="ml-1 h-4 w-4" />
                      </a>
                    )}
                  </h2>
                  <div className="space-y-2">
                    {eventDetails.location.name && (
                      <div className="font-semibold text-gray-900">
                        {eventDetails.location.name}
                      </div>
                    )}
                    <div className="text-gray-700">
                      {eventDetails.location.address1 && (
                        <div>{eventDetails.location.address1}</div>
                      )}
                      {eventDetails.location.cityStateZip && (
                        <div>{eventDetails.location.cityStateZip}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Embedded Map */}
                {getMapUrl() && (
                  <div className="h-64 relative">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      referrerPolicy="no-referrer-when-downgrade"
                      src={getMapUrl()}
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};